import dayjs from 'dayjs';
import { expandOutlookOccurrenceDates } from './outlookRecurrence';
import {
  RULE_JSON_WEEKDAY_LABELS,
  SCHEDULE_SLOT_LABELS,
  WEEKDAY_KEYS,
  WEEKDAY_LABELS,
} from './constants';

const DOW_JS_TO_RULE = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

const NTH_WORD = {
  1: 'first',
  2: 'second',
  3: 'third',
  4: 'fourth',
  5: 'last',
};

function formatOrdinalNth(n) {
  return NTH_WORD[n] || `${n}th`;
}

function formatMonthlyRecurrenceHuman(orc) {
  const mode = orc.monthly?.mode || 'dayOfMonth';
  if (mode === 'dayOfMonth') {
    const d = orc.monthly?.dayOfMonth ?? 1;
    return `on day ${d} of each month`;
  }
  if (mode === 'nthWeekday') {
    const n = orc.monthly?.nth ?? 1;
    const wk = orc.monthly?.weekday || 'MONDAY';
    const dayName = WEEKDAY_LABELS[wk] || wk;
    return `on the ${formatOrdinalNth(n)} ${dayName} of each month`;
  }
  if (mode === 'firstOrLastCalendarDay') {
    const fl = orc.monthly?.firstOrLast === 'last' ? 'last' : 'first';
    return `on the ${fl} calendar day of each month`;
  }
  return 'each month';
}

function formatYearlyRecurrenceHuman(orc) {
  const ym = orc.yearly?.mode || 'date';
  const month = orc.yearly?.month ?? 1;
  const monthName = dayjs()
    .month(month - 1)
    .format('MMMM');
  if (ym === 'date') {
    const d = orc.yearly?.day ?? 1;
    return `every year on ${monthName} ${d}`;
  }
  const n = orc.yearly?.nth ?? 1;
  const wk = orc.yearly?.weekday || 'MONDAY';
  const dayName = WEEKDAY_LABELS[wk] || wk;
  return `every year on the ${formatOrdinalNth(n)} ${dayName} in ${monthName}`;
}

function formatWeeklyDaysHuman(orc) {
  const days = (orc.weekly?.daysOfWeek || [])
    .slice()
    .sort((a, b) => a - b)
    .map(d => RULE_JSON_WEEKDAY_LABELS[DOW_JS_TO_RULE[d]])
    .filter(Boolean);
  return days.length ? days.join(', ') : '';
}

const GRID_WEEKDAY_TO_RULE = {
  MONDAY: 'MON',
  TUESDAY: 'TUE',
  WEDNESDAY: 'WED',
  THURSDAY: 'THU',
  FRIDAY: 'FRI',
  SATURDAY: 'SAT',
  SUNDAY: 'SUN',
};

export function scheduleSlotKeysToRuleSlots(slotKeys) {
  return slotKeys.map(k => (k === 'ALLDAY' ? 'ALL_DAY' : k));
}

function buildOutlookExpandParamsFromForm(form) {
  const orc = form.outlookRecurrence;
  if (!orc) return null;
  return {
    seriesStart: dayjs(form.dates.seriesStart),
    pattern: orc.pattern,
    dailyMode: orc.daily?.mode || 'interval',
    dailyEveryN: orc.daily?.everyNDays ?? 1,
    weeklyEveryN: orc.weekly?.everyNWeeks ?? 1,
    weeklyDays: [...(orc.weekly?.daysOfWeek || [])].sort((a, b) => a - b),
    monthlyMode: orc.monthly?.mode || 'dayOfMonth',
    monthlyDay: orc.monthly?.dayOfMonth ?? 1,
    monthlyNth: orc.monthly?.nth ?? 1,
    monthlyWeekday: orc.monthly?.weekday || 'MONDAY',
    monthlyFirstOrLast: orc.monthly?.firstOrLast || 'first',
    yearlyMode: orc.yearly?.mode || 'date',
    yearlyMonth: orc.yearly?.month ?? 1,
    yearlyDay: orc.yearly?.day ?? 1,
    yearlyNth: orc.yearly?.nth ?? 1,
    yearlyWeekday: orc.yearly?.weekday || 'MONDAY',
    rangeMode: orc.range?.mode || 'endBy',
    rangeEndBy: dayjs(orc.range?.endBy),
  };
}

/**
 * @param {{
 *   id: string,
 *   working: boolean,
 *   form: Record<string, unknown>,
 * }} param0
 */
export function buildRuleFromCustomScheduleForm({ id, working, form }) {
  const ruleSlots = scheduleSlotKeysToRuleSlots(
    form.timeSlots?.slots?.filter(Boolean) || [],
  );

  const type = working ? 'OVERRIDE' : 'EXCEPTION';
  const priority = working ? 4 : 2;
  const action = working ? 'ALLOW' : 'BLOCK';

  const conditions = {};

  if (form.recurring && form.outlookRecurrence) {
    const orc = form.outlookRecurrence;
    const rec = {};

    if (orc.pattern === 'daily') {
      rec.type = 'DAILY';
    } else if (orc.pattern === 'weekly') {
      rec.type = 'WEEKLY';
      const days = (orc.weekly?.daysOfWeek || [])
        .map(d => DOW_JS_TO_RULE[d])
        .filter(Boolean);
      rec.daysOfWeek = days;
    } else if (orc.pattern === 'monthly') {
      rec.type = 'MONTHLY';
      const mode = orc.monthly?.mode || 'dayOfMonth';
      if (mode === 'dayOfMonth') {
        rec.day = orc.monthly?.dayOfMonth ?? 1;
      } else if (mode === 'nthWeekday') {
        rec.weekOfMonth = orc.monthly?.nth ?? 1;
        rec.dayOfWeek = GRID_WEEKDAY_TO_RULE[orc.monthly?.weekday] || 'MON';
      } else if (mode === 'firstOrLastCalendarDay') {
        rec.calendarDayPosition =
          orc.monthly?.firstOrLast === 'last' ? 'LAST' : 'FIRST';
      }
    } else if (orc.pattern === 'yearly') {
      rec.type = 'YEARLY';
      const ym = orc.yearly?.mode || 'date';
      if (ym === 'date') {
        rec.month = orc.yearly?.month ?? 1;
        rec.day = orc.yearly?.day ?? 1;
      } else {
        rec.month = orc.yearly?.month ?? 1;
        rec.weekOfMonth = orc.yearly?.nth ?? 1;
        rec.dayOfWeek = GRID_WEEKDAY_TO_RULE[orc.yearly?.weekday] || 'MON';
      }
    }

    if (Object.keys(rec).length) {
      conditions.recurrence = rec;
    }

    const start = form.dates?.seriesStart;
    const rangeMode = orc.range?.mode;
    const endBy = orc.range?.endBy;
    if (start && rangeMode === 'endBy' && endBy) {
      conditions.effectivePeriod = {
        start,
        end: endBy,
      };
    }
  } else if (!form.recurring) {
    const shape = form.duration?.shape;
    if (shape === 'single' && form.dates?.single) {
      conditions.dates = [form.dates.single];
    } else if (shape === 'range' && form.dates?.start && form.dates?.end) {
      const keys = [];
      let cur = dayjs(form.dates.start).startOf('day');
      const last = dayjs(form.dates.end).startOf('day');
      while (!cur.isAfter(last, 'day')) {
        keys.push(cur.format('YYYY-MM-DD'));
        cur = cur.add(1, 'day');
      }
      conditions.dates = keys;
    }
  }

  return {
    id,
    type,
    priority,
    conditions,
    effect: {
      action,
      slots: ruleSlots,
    },
  };
}

/**
 * @param {Array<{ id: string, name?: string, description?: string, working: boolean, form: Record<string, unknown> }>} schedules
 * @param {string} [timezone]
 */
export function buildRulesEnginePayload(schedules, timezone) {
  const tz =
    timezone ||
    (typeof Intl !== 'undefined' &&
      Intl.DateTimeFormat?.().resolvedOptions?.().timeZone) ||
    'America/New_York';
  return {
    timezone: tz,
    rules: (schedules || []).map(s =>
      buildRuleFromCustomScheduleForm({
        id: s.id,
        working: s.working,
        form: s.form,
      }),
    ),
  };
}

/**
 * Plain-language summary of the rules-engine rule for list UI.
 * @param {{ working?: boolean, form?: Record<string, unknown> } | null | undefined} schedule
 */
export function formatCustomScheduleRuleHumanReadable(schedule) {
  try {
    const form = schedule?.form;
    if (!form || typeof form !== 'object') {
      return '—';
    }

    const working = Boolean(schedule?.working);
    const slots = (form.timeSlots?.slots || []).filter(Boolean);
    const slotText = slots.length
      ? slots.map(k => SCHEDULE_SLOT_LABELS[k] || k).join(', ')
      : 'the selected time slots';

    const availabilityPart = working
      ? `Override — available during ${slotText}`
      : `Not available during ${slotText}`;

    let schedulePart = '';

    if (form.recurring && form.outlookRecurrence) {
      const orc = form.outlookRecurrence;
      const start = form.dates?.seriesStart;
      const startFmt = start
        ? dayjs(start).format('MMM D, YYYY')
        : 'an unspecified start date';

      let patternPart = '';
      if (orc.pattern === 'daily') {
        patternPart = 'Repeats every day';
      } else if (orc.pattern === 'weekly') {
        const w = formatWeeklyDaysHuman(orc);
        patternPart = w ? `Repeats weekly on ${w}` : 'Repeats weekly';
      } else if (orc.pattern === 'monthly') {
        patternPart = `Repeats monthly ${formatMonthlyRecurrenceHuman(orc)}`;
      } else if (orc.pattern === 'yearly') {
        const y = formatYearlyRecurrenceHuman(orc);
        patternPart = y.charAt(0).toUpperCase() + y.slice(1);
      } else {
        patternPart = 'Recurring schedule';
      }

      schedulePart = `${patternPart}, starting ${startFmt}`;
      if (orc.range?.mode === 'endBy' && orc.range?.endBy) {
        schedulePart += `; ends ${dayjs(orc.range.endBy).format('MMM D, YYYY')}`;
      } else if (orc.range?.mode === 'noEnd') {
        schedulePart += '; no end date';
      }
    } else {
      const shape = form.duration?.shape;
      if (shape === 'single' && form.dates?.single) {
        schedulePart = `On ${dayjs(form.dates.single).format('MMM D, YYYY')}`;
      } else if (shape === 'range' && form.dates?.start && form.dates?.end) {
        schedulePart = `From ${dayjs(form.dates.start).format('MMM D, YYYY')} through ${dayjs(form.dates.end).format('MMM D, YYYY')}`;
      } else {
        schedulePart = 'Schedule window not specified';
      }
    }

    return `${availabilityPart}. ${schedulePart}.`;
  } catch {
    return '—';
  }
}

/**
 * Re-hydrate UI state defaults from a persisted `form` payload (output of buildFormPayload).
 * @param {Record<string, unknown>} payload
 * @param {import('dayjs').Dayjs} today
 */
export function hydrateEditorDefaultsFromFormPayload(payload, today) {
  if (!payload || typeof payload !== 'object') return null;

  const name = typeof payload.name === 'string' ? payload.name : '';
  const description =
    typeof payload.description === 'string' ? payload.description : '';
  const working = Boolean(payload.working);
  const recurring = Boolean(payload.recurring);

  let durationShape = 'single';
  if (!recurring && payload.duration?.shape) {
    durationShape = payload.duration.shape === 'range' ? 'range' : 'single';
  }

  let singleDate = today;
  let rangeStart = today;
  let rangeEnd = today.add(1, 'week');

  const dates = payload.dates || {};
  if (recurring && dates.seriesStart) {
    singleDate = dayjs(dates.seriesStart).startOf('day');
  } else if (!recurring && durationShape === 'single' && dates.single) {
    singleDate = dayjs(dates.single).startOf('day');
  } else if (!recurring && durationShape === 'range') {
    if (dates.start) rangeStart = dayjs(dates.start).startOf('day');
    if (dates.end) rangeEnd = dayjs(dates.end).startOf('day');
  }

  const slotKeys = payload.timeSlots?.slots;
  const slotSelection = Array.isArray(slotKeys)
    ? slotKeys.filter(k =>
        ['ALLDAY', 'MORNING', 'AFTERNOON', 'EVENING'].includes(k),
      )
    : [];

  const orc = payload.outlookRecurrence;
  let recPattern = 'weekly';
  let weeklyDays = new Set([1, 2, 3, 4, 5]);
  let monthlyMode = 'dayOfMonth';
  let monthlyDay = 1;
  let monthlyNth = 1;
  let monthlyWeekday = 'MONDAY';
  let monthlyFirstOrLast = 'first';
  let yearlyMode = 'date';
  let yearlyMonth = 1;
  let yearlyDay = 1;
  let yearlyNth = 1;
  let yearlyWeekday = 'MONDAY';
  let rangeMode = 'endBy';
  let rangeEndBy = today.add(1, 'year');

  if (orc && recurring) {
    if (orc.pattern) recPattern = orc.pattern;
    if (Array.isArray(orc.weekly?.daysOfWeek)) {
      weeklyDays = new Set(orc.weekly.daysOfWeek.filter(n => n >= 0 && n <= 6));
    }
    if (orc.monthly?.mode) monthlyMode = orc.monthly.mode;
    if (Number.isFinite(orc.monthly?.dayOfMonth)) {
      monthlyDay = orc.monthly.dayOfMonth;
    }
    if (Number.isFinite(orc.monthly?.nth)) monthlyNth = orc.monthly.nth;
    if (orc.monthly?.weekday && WEEKDAY_KEYS.includes(orc.monthly.weekday)) {
      monthlyWeekday = orc.monthly.weekday;
    }
    if (orc.monthly?.firstOrLast === 'last') {
      monthlyFirstOrLast = 'last';
    } else if (orc.monthly?.firstOrLast === 'first') {
      monthlyFirstOrLast = 'first';
    }
    if (orc.yearly?.mode) yearlyMode = orc.yearly.mode;
    if (Number.isFinite(orc.yearly?.month)) yearlyMonth = orc.yearly.month;
    if (Number.isFinite(orc.yearly?.day)) yearlyDay = orc.yearly.day;
    if (Number.isFinite(orc.yearly?.nth)) yearlyNth = orc.yearly.nth;
    if (orc.yearly?.weekday && WEEKDAY_KEYS.includes(orc.yearly.weekday)) {
      yearlyWeekday = orc.yearly.weekday;
    }
    if (orc.range?.mode === 'noEnd') rangeMode = 'noEnd';
    else rangeMode = 'endBy';
    if (orc.range?.endBy) {
      rangeEndBy = dayjs(orc.range.endBy).startOf('day');
    }
  }

  return {
    name,
    description,
    working,
    recurring,
    durationShape,
    singleDate,
    rangeStart,
    rangeEnd,
    slotSelection,
    recPattern,
    weeklyDays,
    monthlyMode,
    monthlyDay,
    monthlyNth,
    monthlyWeekday,
    monthlyFirstOrLast,
    yearlyMode,
    yearlyMonth,
    yearlyDay,
    yearlyNth,
    yearlyWeekday,
    rangeMode,
    rangeEndBy,
  };
}

export function previewAffectedDateKeysFromForm(form) {
  if (!form.recurring) {
    const shape = form.duration?.shape;
    if (shape === 'single' && form.dates?.single) {
      return [form.dates.single];
    }
    if (shape === 'range' && form.dates?.start && form.dates?.end) {
      const keys = [];
      let cur = dayjs(form.dates.start).startOf('day');
      const last = dayjs(form.dates.end).startOf('day');
      while (!cur.isAfter(last, 'day')) {
        keys.push(cur.format('YYYY-MM-DD'));
        cur = cur.add(1, 'day');
      }
      return keys;
    }
    return [];
  }
  const params = buildOutlookExpandParamsFromForm(form);
  if (!params) return [];
  return expandOutlookOccurrenceDates(params);
}
